import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Users, Heart, Shield, Award } from 'lucide-react';

const values = [
  {
    icon: Users,
    title: 'INCLUSIÓN',
    description: 'Abrimos las puertas a todos los jóvenes sin importar su condición socioeconómica, género o habilidades.',
    color: 'orange'
  },
  {
    icon: Shield,
    title: 'DISCIPLINA',
    description: 'Formamos en la constancia, el respeto y el compromiso como pilares del desarrollo personal.',
    color: 'amber'
  },
  {
    icon: Heart,
    title: 'TRABAJO EN EQUIPO',
    description: 'Cultivamos la colaboración y la solidaridad como valores fundamentales para el éxito colectivo.',
    color: 'orange'
  },
  {
    icon: Award,
    title: 'LIDERAZGO',
    description: 'Desarrollamos habilidades de liderazgo juvenil para la participación activa en la comunidad.',
    color: 'amber'
  }
];

export function ImpactSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      id="impacto" 
      className="py-24 px-4 sm:px-6 lg:px-8 bg-black"
      ref={ref}
      data-testid="impact-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="text-orange-500 font-semibold tracking-widest text-sm">TRANSFORMACIÓN SOCIAL</span>
          <h2 
            className="text-4xl sm:text-5xl font-extrabold text-white mt-2 mb-4"
            style={{ fontFamily: 'Barlow Condensed' }}
            data-testid="impact-title"
          >
            IMPACTO SOCIAL
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
            El deporte como herramienta de transformación social y desarrollo juvenil en nuestra comunidad.
          </p>
        </motion.div>

        {/* Main content */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <img
              src="https://images.pexels.com/photos/6777170/pexels-photo-6777170.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Impacto social del club"
              className="rounded-lg w-full h-[400px] object-cover border border-white/10"
              data-testid="impact-image"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <h3 
              className="text-2xl sm:text-3xl font-bold text-white mb-6"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              MÁS QUE UN CLUB DEPORTIVO
            </h3>
            <div className="space-y-4 text-zinc-400">
              <p>
                En <strong className="text-white">Jotuns Basketball Club</strong> entendemos que el baloncesto 
                es una poderosa herramienta para el desarrollo integral de los jóvenes. Nuestro trabajo se 
                articula con las <strong className="text-orange-500">políticas públicas de juventud</strong> del 
                municipio de Sesquilé.
              </p>
              <p>
                A través del deporte, contribuimos a la <strong className="text-white">prevención de riesgos sociales</strong>, 
                ofreciendo a los jóvenes un espacio seguro de crecimiento, aprendizaje y socialización.
              </p>
              <p>
                Como organización vinculada a la <strong className="text-orange-500">Plataforma Municipal de Juventud</strong>, 
                participamos activamente en la construcción de una sociedad más equitativa e inclusiva.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Values Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((value, index) => (
            <motion.div
              key={value.title}
              className="bento-item p-6 rounded-lg text-center card-hover"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
              data-testid={`value-${value.title.toLowerCase()}`}
            >
              <div className={`w-16 h-16 mx-auto mb-4 rounded-lg flex items-center justify-center ${
                value.color === 'orange' ? 'bg-orange-500/20' : 'bg-amber-500/20'
              }`}>
                <value.icon className={value.color === 'orange' ? 'text-orange-500' : 'text-amber-500'} size={32} />
              </div>
              <h4 
                className="text-xl font-bold text-white mb-2"
                style={{ fontFamily: 'Barlow Condensed' }}
              >
                {value.title}
              </h4>
              <p className="text-zinc-400 text-sm">{value.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
